import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { TooltipDirective } from '../../atoms/tooltip/tooltip.directive';
import { IndicatorState } from '../../../models/indicator.models';
import { IndicatorStates } from '../../../constants/indicators.constants';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TooltipDirective,
    TranslocoDirective,
    TranslocoDecimalPipe],
  selector: 'ui-badge-indicator',
  template: `
    <ng-container *transloco="let t">
      <div class="w-full flex justify-end">
        <div
          class="flex flex-row items-center justify-center w-fit gap-x-2 px-3 rounded-full"
          [class]="stateClassMap[state()].badge"
          uiTooltip
          [tooltipTitle]="hasNominal() ? tooltipTitleTemplate : null"
          [tooltipTitleContext]="hasNominal() ? { $implicit: nominal() } : undefined"
          [tooltipTrigger]="hasNominal() && !isDisabled() ? 'hover' : null"
        >
          <span
            class="py-1 pr-1 text-xs min-w-max flex gap-x-0.5 justify-end items-end"
            >{{ value() | translocoDecimal:{ minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false  } }}
            <span class="text-xs leading-1">{{ units() }}</span></span
          >
          @if(percentage()){
            <div
              class="w-0.5 h-7"
              [class]="stateClassMap[state()].separator"
            ></div>
            <span class="py-1 text-xs min-w-9"
              >{{ percentage() | translocoDecimal:{ minimumFractionDigits: 0, maximumFractionDigits: 3, useGrouping: false  } }}%</span
            >
          }
        </div>
      </div>

      <ng-template #tooltipTitleTemplate let-data>
        <div class="flex flex-col text-xs">
          <span>
            {{ t(tooltipLabel()) }}:
            {{
              isDisabled()
                ? '--'
                : (data | translocoDecimal:{ minimumFractionDigits: 0, maximumFractionDigits: 3, useGrouping: false  }) || t('DISTRIBUTION_NETWORK.TABLE.WITHOUT_DATA')
            }}
            {{ unitsReference() }}
          </span>
        </div>
      </ng-template>
    </ng-container>
  `,
})
export class BadgeIndicatorComponent {
  state = input<IndicatorState>(IndicatorStates.Default);
  value = input<number>(0);
  nominal = input<number>();
  percentage = input<number>(0);
  units = input<string>();
  unitsReference = input<string>();
  tooltipLabel = input<string>('DISTRIBUTION_NETWORK.TABLE.NOMINAL');

  isDisabled = computed<boolean>(
    () => this.state() === IndicatorStates.Disabled,
  );

  // don't show tooltip when nominal is not provided or value is zero
  hasNominal = computed<boolean>(() => this.nominal() != null && this.nominal() !== 0);

  trigger = computed(() => (this.isDisabled() ? null : 'hover'));

  stateClassMap: Record<IndicatorState, { separator: string; badge: string }> =
    {
      active: { separator: 'bg-neutral-6', badge: 'bg-gray-100 text-black' },
      disabled: {
        separator: 'bg-neutral-6',
        badge: 'bg-gray-100 text-neutral-6',
      },
      danger: {
        separator: 'bg-red-4',
        badge: 'bg-red-2 text-black',
      },
    };
}
